import { Mutex } from 'async-mutex';
import { AxiosError } from 'axios';
import { UiDictionariesClient } from '~/app-modules/core/clients/ui-dictionaries-client';
import { StrictMap } from '~/app-modules/core/utils/strict-map';
import { UiDictionaryType } from '~/app-modules/core/enums/UiDictionaryType';

const KNOWN_TYPES = Object.values(UiDictionaryType);
const KNOWN_TYPES_SET = new Set<string>(KNOWN_TYPES);

type TUiItem<TValue> = { label: string; value: TValue };

@singleton()
export class LocalizationsStore {
  constructor(private _uiDictionariesClient: UiDictionariesClient) {}

  private _mutex = new Mutex();

  private _dictionary = shallowReactive(
    new StrictMap<UiDictionaryType, StrictMap<string, string>>(),
  );

  async getDictionaryByType(type: UiDictionaryType): Promise<StrictMap<string, string>> {
    if (this._dictionary.size !== 0) return this._dictionary.strictGet(type);

    await this.loadDictionaries();

    return this._dictionary.strictGet(type);
  }

  /**
   * Делает список { label, value } из enum.
   */
  async getLocalizedValuesByEnum<TEnum, T extends { [p: string]: TEnum } | TEnum[]>(
    enm: T,
    type: UiDictionaryType,
  ): Promise<TUiItem<TEnum>[]> {
    if (this._dictionary.size === 0) {
      await this.loadDictionaries();
    }
    const values = Object.values(enm) as TEnum[];

    const localizedEnumDictionary = await this.getDictionaryByType(type);

    return values.map((value) => {
      const valueAsString = String(value);
      let label = localizedEnumDictionary.get(valueAsString);
      if (!label) {
        console.warn(
          `[UiDictionaries] Missing/empty translation for "${type}" key "${value}". Using key fallback.`,
        );
        label = valueAsString;
      }

      return {
        value,
        label,
      };
    });
  }

  async loadDictionaries() {
    try {
      await this._mutex.runExclusive(async () => {
        if (this._dictionary.size !== 0) return;

        const uiDictionaries = await this._uiDictionariesClient.getUiDictionaries();

        // Произошли ломающие изменения в API
        const missingTypes = KNOWN_TYPES.filter((t) => !uiDictionaries[t]);
        if (missingTypes.length) {
          throw new Error(
            `[UiDictionaries] BREAKING: missing dictionary types: ${missingTypes.join(', ')}`,
          );
        }

        // Произошло расширение в API
        for (const [typeRaw, dictPairs] of Object.entries(uiDictionaries)) {
          if (!KNOWN_TYPES_SET.has(typeRaw)) {
            console.warn('[UiDictionaries] Unknown dictionary type (non-breaking):', typeRaw);
            continue;
          }

          this._dictionary.set(
            typeRaw as UiDictionaryType,
            new StrictMap<string, string>(dictPairs),
          );
        }
      });
    } catch (error) {
      if (error instanceof AxiosError) return;
      throw error;
    }
  }
}
